import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    ArrowLeftIcon,
    MapPinIcon,
    CalendarIcon,
    ClockIcon,
    TruckIcon,
    TicketIcon,
    CurrencyDollarIcon,
    UserCircleIcon,
    PhoneIcon,
    EnvelopeIcon,
    XCircleIcon,
    NoSymbolIcon,
    PlayIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { getRideOfferDetails, cancelRideOffer, suspendRideOffer } from "../api/rideBookingsTripsApi";
import { extractApiError } from "@/lib/api-error";

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        AVAILABLE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FULL: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        IN_PROGRESS: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        COMPLETED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        EXPIRED: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        SUSPENDED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        CONFIRMED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        PENDING_PAYMENT: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        PAID: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        REFUNDED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s.replace(/_/g, " ")}
        </span>
    );
};

const VehicleBadge = ({ type }: { type: string }) => {
    const t = type ?? "UNKNOWN";
    const styles: Record<string, string> = {
        KEKE: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        REGULAR: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        COMFORT: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        LUXURIOUS: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
    };
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${styles[t] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {t}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
};

// ============================================================================
// Component
// ============================================================================

export const RideOfferDetailsRoute = () => {
    const { rideOfferId } = useParams<{ rideOfferId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const [cancelModal, setCancelModal] = useState(false);
    const [suspendModal, setSuspendModal] = useState<boolean | null>(null); // null = closed, true = resuming, false = suspending
    const [actionReason, setActionReason] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["rideOfferDetails", rideOfferId],
        queryFn: () => getRideOfferDetails(rideOfferId!),
        enabled: !!rideOfferId,
    });

    const cancelMutation = useMutation({
        mutationFn: (reason: string) => cancelRideOffer(rideOfferId!, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["rideOfferDetails", rideOfferId] });
            queryClient.invalidateQueries({ queryKey: ["rideOffers"] });
            setCancelModal(false);
            setActionReason("");
        },
    });

    const suspendMutation = useMutation({
        mutationFn: ({ suspend, reason }: { suspend: boolean; reason: string }) =>
            suspendRideOffer(rideOfferId!, suspend, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["rideOfferDetails", rideOfferId] });
            queryClient.invalidateQueries({ queryKey: ["rideOffers"] });
            setSuspendModal(null);
            setActionReason("");
        },
    });

    const ride = data?.data;

    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
            </div>
        );
    }

    if (error || !ride) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
                <XCircleIcon className="h-12 w-12 text-rose-400" />
                <p className="text-sm text-slate-600 dark:text-slate-400">{error ? extractApiError(error) : "Ride offer not found"}</p>
                <Button variant="outline" onClick={() => navigate("/ride-bookings-trips/rides")}>
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />
                    Back to Ride Offers
                </Button>
            </div>
        );
    }

    const canSuspend = !["CANCELLED", "COMPLETED", "EXPIRED", "IN_PROGRESS"].includes(ride.status);
    const canCancel = !["CANCELLED", "COMPLETED", "EXPIRED"].includes(ride.status);
    const isSuspended = ride.status === "SUSPENDED";
    const canManageRideOffers = hasPermission(SECTION_PERMISSIONS.RIDE_OFFERS_MANAGE[0]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate("/ride-bookings-trips/rides")}>
                        <ArrowLeftIcon className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Ride Offer Details</h1>
                            <StatusBadge status={ride.status} />
                        </div>
                        <p className="mt-1 font-mono text-xs text-slate-400">{ride.rideOfferId}</p>
                    </div>
                </div>
                {canManageRideOffers ? (
                    <div className="flex items-center gap-2">
                        {isSuspended ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400"
                                onClick={() => setSuspendModal(true)}
                            >
                                <PlayIcon className="mr-1.5 h-4 w-4" />
                                Resume
                            </Button>
                        ) : canSuspend ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-amber-600 hover:bg-amber-50 dark:text-amber-400"
                                onClick={() => setSuspendModal(false)}
                            >
                                <NoSymbolIcon className="mr-1.5 h-4 w-4" />
                                Suspend
                            </Button>
                        ) : null}
                        {canCancel ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-rose-600 hover:bg-rose-50 dark:text-rose-400"
                                onClick={() => setCancelModal(true)}
                            >
                                <XCircleIcon className="mr-1.5 h-4 w-4" />
                                Cancel Ride
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>

            {/* Route & Stats */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {/* Route Card */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Route Information</h2>
                    </div>
                    <div className="space-y-4 p-6">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 flex flex-col items-center gap-0.5">
                                <div className="h-3 w-3 rounded-full border-2 border-emerald-500 bg-emerald-100" />
                                <div className="h-10 w-0.5 bg-slate-200 dark:bg-slate-700" />
                                <div className="h-3 w-3 rounded-full border-2 border-rose-500 bg-rose-100" />
                            </div>
                            <div className="flex-1 space-y-4">
                                <div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Origin</p>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{ride.origin}</p>
                                    <p className="mt-0.5 font-mono text-xs text-slate-400">{ride.originLatitude?.toFixed(4)}, {ride.originLongitude?.toFixed(4)}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Destination</p>
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{ride.destination}</p>
                                    <p className="mt-0.5 font-mono text-xs text-slate-400">{ride.destinationLatitude?.toFixed(4)}, {ride.destinationLongitude?.toFixed(4)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 dark:border-slate-800 sm:grid-cols-4">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Departure</p>
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{formatDate(ride.departureTime)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <ClockIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Est. Arrival</p>
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{formatDate(ride.estimatedArrivalTime)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <TruckIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Vehicle</p>
                                    <VehicleBadge type={ride.vehicleType} />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <TicketIcon className="h-4 w-4 text-slate-400" />
                                <div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Seats</p>
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{ride.availableSeats} / {ride.vehicleCapacity} available</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Card */}
                <div className="space-y-4">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Statistics</h2>
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-6">
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Bookings</p>
                                <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{ride.stats.totalBookings}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Passengers</p>
                                <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{ride.stats.totalPassengers}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Revenue</p>
                                <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(ride.stats.totalRevenue, ride.currency)}</p>
                            </div>
                            <div>
                                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Price/Seat</p>
                                <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{formatCurrency(ride.pricePerSeat, ride.currency)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Driver Card */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Driver</h2>
                        </div>
                        <div className="space-y-3 p-6">
                            <div className="flex items-center gap-3">
                                <UserCircleIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{ride.driver.firstName} {ride.driver.lastName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <EnvelopeIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{ride.driver.email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <PhoneIcon className="h-5 w-5 text-slate-400" />
                                <span className="text-sm text-slate-600 dark:text-slate-400">{ride.driver.phone}</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-2 w-full text-primary-600"
                                onClick={() => navigate(`/drivers/${ride.driver.driverId}`)}
                            >
                                View Driver Profile →
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bookings Table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Bookings ({ride.bookings.length})
                    </h2>
                </div>
                {ride.bookings.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                        <TicketIcon className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No bookings yet</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-slate-100 dark:border-slate-800">
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Rider</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Seats</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Booked At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {ride.bookings.map((booking) => (
                                    <tr
                                        key={booking.bookingId}
                                        onClick={() => navigate(`/ride-bookings-trips/bookings/${booking.bookingId}`)}
                                        className="cursor-pointer transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">{booking.riderName}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{booking.numberOfSeats}</td>
                                        <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">{formatCurrency(booking.totalAmount)}</td>
                                        <td className="px-4 py-3"><StatusBadge status={booking.status} /></td>
                                        <td className="px-4 py-3"><StatusBadge status={booking.paymentStatus} /></td>
                                        <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{formatDate(booking.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Timestamps */}
            <div className="flex items-center gap-6 text-xs text-slate-400">
                <span>Created: {formatDate(ride.createdAt)}</span>
                <span>Updated: {formatDate(ride.updatedAt)}</span>
            </div>

            {/* Cancel Modal */}
            {cancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setCancelModal(false); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Cancel Ride Offer</h3>
                        <p className="mt-2 text-sm text-slate-500">This will cancel the ride and all pending bookings.</p>
                        <textarea
                            rows={3}
                            placeholder="Cancellation reason..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setCancelModal(false); setActionReason(""); }}>Go Back</Button>
                            <Button
                                size="sm"
                                className="bg-rose-600 hover:bg-rose-700"
                                disabled={!actionReason.trim() || cancelMutation.isPending}
                                onClick={() => cancelMutation.mutate(actionReason)}
                            >
                                {cancelMutation.isPending ? "Cancelling..." : "Cancel Ride"}
                            </Button>
                        </div>
                        {cancelMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(cancelMutation.error)}</p>}
                    </div>
                </div>
            )}

            {/* Suspend/Resume Modal */}
            {suspendModal !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => { setSuspendModal(null); setActionReason(""); }}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            {suspendModal ? "Resume" : "Suspend"} Ride Offer
                        </h3>
                        <textarea
                            rows={3}
                            placeholder="Reason (optional)..."
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="mt-4 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <Button variant="outline" size="sm" onClick={() => { setSuspendModal(null); setActionReason(""); }}>Go Back</Button>
                            <Button
                                size="sm"
                                className={suspendModal ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}
                                disabled={suspendMutation.isPending}
                                onClick={() => suspendMutation.mutate({ suspend: !suspendModal, reason: actionReason })}
                            >
                                {suspendMutation.isPending ? "Processing..." : suspendModal ? "Resume Ride" : "Suspend Ride"}
                            </Button>
                        </div>
                        {suspendMutation.isError && <p className="mt-3 text-sm text-rose-600">{extractApiError(suspendMutation.error)}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};
